import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/ui/design-system/components/ui/dialog';
import { Button } from '@/ui/design-system/components/ui/button';
import { Input } from '@/ui/design-system/components/ui/input';
import { Label } from '@/ui/design-system/components/ui/label';
import { useRenameApiKey } from 'utils/hooks/apiKeys';
import toast from 'react-hot-toast';

function EditKeyDialog({ open, onOpenChange, apiKey }) {
    const [name, setName] = useState('');
    const renameKey = useRenameApiKey();

    useEffect(() => {
        if (apiKey) {
            setName(apiKey.name || '');
        }
    }, [apiKey]);

    const handleSave = async () => {
        try {
            await renameKey.mutateAsync({
                keyId: apiKey.id,
                name: name.trim(),
            });
            toast.success('API key renamed', {
                icon: '✅',
                position: 'top-right',
            });
            onOpenChange(false);
        } catch {
            toast.error('Failed to rename API key');
        }
    };

    const handleClose = (value) => {
        if (!value) {
            renameKey.reset();
        }
        onOpenChange(value);
    };

    const unchanged = name.trim() === (apiKey?.name || '');

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Edit API Key Name</DialogTitle>
                    <DialogDescription>
                        Update the name of your API key. This won&apos;t affect
                        the key itself.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2">
                    <Label htmlFor="edit-key-name">Key Name</Label>
                    <Input
                        id="edit-key-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (
                                e.key === 'Enter' &&
                                name.trim() &&
                                !unchanged
                            ) {
                                handleSave();
                            }
                        }}
                    />
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => handleClose(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={
                            !name.trim() || unchanged || renameKey.isLoading
                        }
                        onClick={handleSave}
                    >
                        {renameKey.isLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default EditKeyDialog;
